import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { customerService } from "../../services/customerService";
import {
    Customer,
    CreateCustomerRequest,
    UpdateCustomerRequest,
    SearchParams,
} from "../../types";

// Define the initial state interface
interface CustomersState {
    customers: Customer[];
    selectedCustomer: Customer | null;
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;
    searchParams: SearchParams;
    totalCount: number;
}

// Define the initial state
const initialState: CustomersState = {
    customers: [],
    selectedCustomer: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    searchParams: { skip: 0, limit: 100 },
    totalCount: 0,
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
    "customers/fetchCustomers",
    async (params: SearchParams = {}, { rejectWithValue }) => {
        try {
            const response = await customerService.getCustomers(params);
            return {
                customers: response.customers,
                total: response.total,
                params
            };
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch customers"
            );
        }
    }
);

export const fetchCustomerById = createAsyncThunk(
    "customers/fetchCustomerById",
    async (customerId: string, { rejectWithValue }) => {
        try {
            const customer = await customerService.getCustomerById(customerId);
            return customer;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch customer");
        }
    }
);

export const createCustomer = createAsyncThunk(
    "customers/createCustomer",
    async (
        customerData: CreateCustomerRequest,
        { rejectWithValue, dispatch }
    ) => {
        try {
            const newCustomer = await customerService.createCustomer(
                customerData
            );
            // Refresh the customers list
            dispatch(fetchCustomers({}));
            return newCustomer;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to create customer"
            );
        }
    }
);

export const updateCustomer = createAsyncThunk(
    "customers/updateCustomer",
    async (
        { id, data }: { id: string; data: UpdateCustomerRequest },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const updatedCustomer = await customerService.updateCustomer(
                id,
                data
            );
            // Refresh the customers list
            dispatch(fetchCustomers({}));
            return updatedCustomer;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update customer"
            );
        }
    }
);

export const deleteCustomer = createAsyncThunk(
    "customers/deleteCustomer",
    async (customerId: string, { rejectWithValue, dispatch }) => {
        try {
            await customerService.deleteCustomer(customerId);
            // Refresh the customers list
            dispatch(fetchCustomers({}));
            return customerId;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to delete customer"
            );
        }
    }
);

export const searchCustomers = createAsyncThunk(
    "customers/searchCustomers",
    async (searchTerm: string, { rejectWithValue }) => {
        try {
            const customers = await customerService.searchCustomers({
                q: searchTerm,
            });
            return customers;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to search customers"
            );
        }
    }
);

// Create the customers slice
const customersSlice = createSlice({
    name: "customers",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedCustomer: (
            state,
            action: PayloadAction<Customer | null>
        ) => {
            state.selectedCustomer = action.payload;
        },
        updateSearchParams: (
            state,
            action: PayloadAction<Partial<SearchParams>>
        ) => {
            state.searchParams = { ...state.searchParams, ...action.payload };
        },
        clearSelectedCustomer: (state) => {
            state.selectedCustomer = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch customers
            .addCase(fetchCustomers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.customers = action.payload.customers;
                state.searchParams = {
                    ...state.searchParams,
                    ...action.payload.params,
                };
                state.totalCount = action.payload.total;
                state.error = null;
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch customer by ID
            .addCase(fetchCustomerById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCustomerById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedCustomer = action.payload;
                state.error = null;
            })
            .addCase(fetchCustomerById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create customer
            .addCase(createCustomer.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createCustomer.fulfilled, (state) => {
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.payload as string;
            })
            // Update customer
            .addCase(updateCustomer.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateCustomer.fulfilled, (state) => {
                state.isUpdating = false;
                state.error = null;
            })
            .addCase(updateCustomer.rejected, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload as string;
            })
            // Delete customer
            .addCase(deleteCustomer.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteCustomer.fulfilled, (state) => {
                state.isDeleting = false;
                state.error = null;
            })
            .addCase(deleteCustomer.rejected, (state, action) => {
                state.isDeleting = false;
                state.error = action.payload as string;
            })
            // Search customers
            .addCase(searchCustomers.fulfilled, (state, action) => {
                state.customers = action.payload;
                state.totalCount = action.payload.length;
            });
    },
});

// Export actions
export const {
    clearError,
    setSelectedCustomer,
    updateSearchParams,
    clearSelectedCustomer,
} = customersSlice.actions;

// Export selectors
export const selectCustomers = (state: any) => state.customers.customers;
export const selectSelectedCustomer = (state: any) =>
    state.customers.selectedCustomer;
export const selectCustomersLoading = (state: any) => state.customers.isLoading;
export const selectCustomersError = (state: any) => state.customers.error;
export const selectCustomersSearchParams = (state: any) =>
    state.customers.searchParams;
export const selectCustomersCreating = (state: any) =>
    state.customers.isCreating;
export const selectCustomersUpdating = (state: any) =>
    state.customers.isUpdating;
export const selectCustomersDeleting = (state: any) =>
    state.customers.isDeleting;
export const selectCustomersTotalCount = (state: any) =>
    state.customers.totalCount;

// Export reducer
export default customersSlice.reducer;
